// soundeffects.ts ↔ game/soundeffects.h (the Sfx id space) + game/misc.cpp
// :938-1138 (the hardwired sfx-category variant tables + SfxFromCategory).
//
// SIM-side because the ids live in sim data too: the per-unit-type consts
// tables (each InitClass, e.g. Miner.cpp:38-46) and the replicator's DELAYED
// kmidPlaySfx messages (Replicator.cpp:370-386), whose SfxFromCategory pick is
// SERIALIZED into the message image — sim state, so the pick must be drawn
// from ctx.rngAsync at the exact C++ point. client/src/audio/sfx.ts re-exports
// this module for the UI/audio layers.
//
// The Sfx enum ORDER is the contract: a sfx id indexes the SfxEntries pack
// record (3 bytes per entry — audio/soundmgr.ts), whose nSound byte then
// indexes the "soundfiles" name list. 184 ids ↔ 184 SfxEntries records in the
// shipped game/htsfx.pdb. Ids are mechanically extracted from soundeffects.h.
//
// VARIANT SELECTION IS ASYNC-RNG (sim-quirks B19): SfxFromCategory picks with
// GetAsyncRandom (game/misc.cpp:612 glAsyncSeed — the SimContext.rngAsync
// instance), DELIBERATELY out of lockstep sync; wiring the sync LCG here would
// desync multiplayer.

import type { Rng } from '../math/rng.ts';

// game/soundeffects.h:5-190 enum Sfx
export const ksfxNothing = 0; // soundeffects.h:6
export const ksfxGalaxiteProcessorAbortRepair = 1; // soundeffects.h:7
export const ksfxGalaxiteProcessorDamaged = 2; // soundeffects.h:8
export const ksfxGalaxiteProcessorDestroyed = 3; // soundeffects.h:9
export const ksfxGalaxiteProcessorDoorOpening = 4; // soundeffects.h:10
export const ksfxGalaxiteProcessorDoorClosing = 5; // soundeffects.h:11
export const ksfxGalaxiteProcessorRepair = 6; // soundeffects.h:12
export const ksfxGalaxiteProcessorSelect = 7; // soundeffects.h:13
export const ksfxGalaxiteWarehouseAbortRepair = 8; // soundeffects.h:14
export const ksfxGalaxiteWarehouseDamaged = 9; // soundeffects.h:15
export const ksfxGalaxiteWarehouseDestroyed = 10; // soundeffects.h:16
export const ksfxGalaxiteWarehouseRepair = 11; // soundeffects.h:17
export const ksfxGalaxiteWarehouseSelect = 12; // soundeffects.h:18
export const ksfxGalaxiteWarehouseTooFull = 13; // soundeffects.h:19
export const ksfxGalaxMinerMine = 14; // soundeffects.h:20
export const ksfxGalaxMinerUnderAttack = 15; // soundeffects.h:21
export const ksfxGalaxMinerDeliver = 16; // soundeffects.h:22
export const ksfxGameBaseUnderAttack = 17; // soundeffects.h:23
export const ksfxGameCreditsDecreasing = 18; // soundeffects.h:24
export const ksfxGameCreditsIncreasing = 19; // soundeffects.h:25
export const ksfxGameLevelResults = 20; // soundeffects.h:26
export const ksfxGameLoseLevel = 21; // soundeffects.h:27
export const ksfxGameNewVehicleOptions = 22; // soundeffects.h:28
export const ksfxGameNewRecruitOptions = 23; // soundeffects.h:29
export const ksfxGameNewStructureOptions = 24; // soundeffects.h:30
export const ksfxGameWinLevel = 25; // soundeffects.h:31
export const ksfxGuiBuildMenuHide = 26; // soundeffects.h:32
export const ksfxGuiBuildMenuSelectItem = 27; // soundeffects.h:33
export const ksfxGuiBuildMenuShow = 28; // soundeffects.h:34
export const ksfxGuiButtonTap = 29; // soundeffects.h:35
export const ksfxGuiCheckBoxTap = 30; // soundeffects.h:36
export const ksfxGuiEditBoxCharacterEntered = 31; // soundeffects.h:37
export const ksfxGuiFormHide = 32; // soundeffects.h:38
export const ksfxGuiFormShow = 33; // soundeffects.h:39
export const ksfxGuiMissionTextCharOutput = 34; // soundeffects.h:40
export const ksfxGuiScrollingListSelectItem = 35; // soundeffects.h:41
export const ksfxHeadquartersAbortConstruction = 36; // soundeffects.h:42
export const ksfxHeadquartersAbortRepair = 37; // soundeffects.h:43
export const ksfxHeadquartersConstruct = 38; // soundeffects.h:44
export const ksfxHeadquartersDamaged = 39; // soundeffects.h:45
export const ksfxHeadquartersDestroyed = 40; // soundeffects.h:46
export const ksfxHeadquartersRepair = 41; // soundeffects.h:47
export const ksfxHeadquartersSelect = 42; // soundeffects.h:48
export const ksfxHeadquartersStructureReady = 43; // soundeffects.h:49
export const ksfxHumanResourceCenterAbortRecruiting = 44; // soundeffects.h:50
export const ksfxHumanResourceCenterAbortRepair = 45; // soundeffects.h:51
export const ksfxHumanResourceCenterDamaged = 46; // soundeffects.h:52
export const ksfxHumanResourceCenterDestroyed = 47; // soundeffects.h:53
export const ksfxHumanResourceCenterRecruit = 48; // soundeffects.h:54
export const ksfxHumanResourceCenterRepair = 49; // soundeffects.h:55
export const ksfxHumanResourceCenterSelect = 50; // soundeffects.h:56
export const ksfxHumanResourceCenterUnitReady = 51; // soundeffects.h:57
export const ksfxLightTankFire = 52; // soundeffects.h:58
export const ksfxLightTankImpact = 53; // soundeffects.h:59
export const ksfxMachineGunInfantryFire = 54; // soundeffects.h:60
export const ksfxMachineGunTowerAbortRepair = 55; // soundeffects.h:61
export const ksfxMachineGunTowerAttack = 56; // soundeffects.h:62
export const ksfxMachineGunTowerDamaged = 57; // soundeffects.h:63
export const ksfxMachineGunTowerDestroyed = 58; // soundeffects.h:64
export const ksfxMachineGunTowerFire = 59; // soundeffects.h:65
export const ksfxMachineGunTowerRepair = 60; // soundeffects.h:66
export const ksfxMachineGunTowerSelect = 61; // soundeffects.h:67
export const ksfxMachineGunVehicleFire = 62; // soundeffects.h:68
export const ksfxMediumTankFire = 63; // soundeffects.h:69
export const ksfxMediumTankImpact = 64; // soundeffects.h:70
export const ksfxMobileHeadquartersDeploy = 65; // soundeffects.h:71
export const ksfxRadarAbortRepair = 66; // soundeffects.h:72
export const ksfxRadarDamaged = 67; // soundeffects.h:73
export const ksfxRadarDestroyed = 68; // soundeffects.h:74
export const ksfxRadarRepair = 69; // soundeffects.h:75
export const ksfxRadarSelect = 70; // soundeffects.h:76
export const ksfxReactorAbortRepair = 71; // soundeffects.h:77
export const ksfxReactorDamaged = 72; // soundeffects.h:78
export const ksfxReactorDestroyed = 73; // soundeffects.h:79
export const ksfxReactorPowerTooLow = 74; // soundeffects.h:80
export const ksfxReactorRepair = 75; // soundeffects.h:81
export const ksfxReactorSelect = 76; // soundeffects.h:82
export const ksfxResearchCenterAbortRepair = 77; // soundeffects.h:83
export const ksfxResearchCenterDamaged = 78; // soundeffects.h:84
export const ksfxResearchCenterDestroyed = 79; // soundeffects.h:85
export const ksfxResearchCenterRepair = 80; // soundeffects.h:86
export const ksfxResearchCenterSelect = 81; // soundeffects.h:87
export const ksfxRocketInfantryFire = 82; // soundeffects.h:88
export const ksfxRocketInfantryImpact = 83; // soundeffects.h:89
export const ksfxRocketTowerAbortRepair = 84; // soundeffects.h:90
export const ksfxRocketTowerAttack = 85; // soundeffects.h:91
export const ksfxRocketTowerDamaged = 86; // soundeffects.h:92
export const ksfxRocketTowerDestroyed = 87; // soundeffects.h:93
export const ksfxRocketTowerFire = 88; // soundeffects.h:94
export const ksfxRocketTowerImpact = 89; // soundeffects.h:95
export const ksfxRocketTowerRepair = 90; // soundeffects.h:96
export const ksfxRocketTowerSelect = 91; // soundeffects.h:97
export const ksfxRocketVehicleFire = 92; // soundeffects.h:98
export const ksfxRocketVehicleImpact = 93; // soundeffects.h:99
export const ksfxTakeoverSpecialistStructureCaptured = 94; // soundeffects.h:100
export const ksfxVehicleTransportStationAbortManufacture = 95; // soundeffects.h:101
export const ksfxVehicleTransportStationAbortRepair = 96; // soundeffects.h:102
export const ksfxVehicleTransportStationDamaged = 97; // soundeffects.h:103
export const ksfxVehicleTransportStationDestroyed = 98; // soundeffects.h:104
export const ksfxVehicleTransportStationManufacture = 99; // soundeffects.h:105
export const ksfxVehicleTransportStationRepair = 100; // soundeffects.h:106
export const ksfxVehicleTransportStationSelect = 101; // soundeffects.h:107
export const ksfxVehicleTransportStationVehicleReady = 102; // soundeffects.h:108
export const ksfxVehicleDestroyed = 103; // soundeffects.h:109
export const ksfxInfantryDestroyed0 = 104; // soundeffects.h:110
export const ksfxInfantryDestroyed1 = 105; // soundeffects.h:111
export const ksfxInfantryDestroyed2 = 106; // soundeffects.h:112
export const ksfxInfantryDestroyed3 = 107; // soundeffects.h:113
export const ksfxInfantryDestroyed4 = 108; // soundeffects.h:114
export const ksfxMale01Select0 = 109; // soundeffects.h:115
export const ksfxMale01Select1 = 110; // soundeffects.h:116
export const ksfxMale01Select2 = 111; // soundeffects.h:117
export const ksfxMale01Select3 = 112; // soundeffects.h:118
export const ksfxMale01Move0 = 113; // soundeffects.h:119
export const ksfxMale01Move1 = 114; // soundeffects.h:120
export const ksfxMale01Move2 = 115; // soundeffects.h:121
export const ksfxMale01Move3 = 116; // soundeffects.h:122
export const ksfxMale01Attack0 = 117; // soundeffects.h:123
export const ksfxMale01Attack1 = 118; // soundeffects.h:124
export const ksfxMale01Attack2 = 119; // soundeffects.h:125
export const ksfxMale01Attack3 = 120; // soundeffects.h:126
export const ksfxMale03Select0 = 121; // soundeffects.h:127
export const ksfxMale03Select1 = 122; // soundeffects.h:128
export const ksfxMale03Select2 = 123; // soundeffects.h:129
export const ksfxMale03Select3 = 124; // soundeffects.h:130
export const ksfxMale03Move0 = 125; // soundeffects.h:131
export const ksfxMale03Move1 = 126; // soundeffects.h:132
export const ksfxMale03Move2 = 127; // soundeffects.h:133
export const ksfxMale03Move3 = 128; // soundeffects.h:134
export const ksfxMale03Attack0 = 129; // soundeffects.h:135
export const ksfxMale03Attack1 = 130; // soundeffects.h:136
export const ksfxMale03Attack2 = 131; // soundeffects.h:137
export const ksfxMale03Attack3 = 132; // soundeffects.h:138
export const ksfxMale06Select0 = 133; // soundeffects.h:139
export const ksfxMale06Select1 = 134; // soundeffects.h:140
export const ksfxMale06Select2 = 135; // soundeffects.h:141
export const ksfxMale06Select3 = 136; // soundeffects.h:142
export const ksfxMale06Move0 = 137; // soundeffects.h:143
export const ksfxMale06Move1 = 138; // soundeffects.h:144
export const ksfxMale06Move2 = 139; // soundeffects.h:145
export const ksfxMale06Move3 = 140; // soundeffects.h:146
export const ksfxMale06Attack0 = 141; // soundeffects.h:147
export const ksfxMale06Attack1 = 142; // soundeffects.h:148
export const ksfxMale06Attack2 = 143; // soundeffects.h:149
export const ksfxMale06Attack3 = 144; // soundeffects.h:150
export const ksfxMajor01Select0 = 145; // soundeffects.h:151
export const ksfxMajor01Select1 = 146; // soundeffects.h:152
export const ksfxMajor01Select2 = 147; // soundeffects.h:153
export const ksfxMajor01Select3 = 148; // soundeffects.h:154
export const ksfxMajor01Move0 = 149; // soundeffects.h:155
export const ksfxMajor01Move1 = 150; // soundeffects.h:156
export const ksfxMajor01Move2 = 151; // soundeffects.h:157
export const ksfxMajor01Move3 = 152; // soundeffects.h:158
export const ksfxMajor01Attack0 = 153; // soundeffects.h:159
export const ksfxMajor01Attack1 = 154; // soundeffects.h:160
export const ksfxMajor01Attack2 = 155; // soundeffects.h:161
export const ksfxMajor01Attack3 = 156; // soundeffects.h:162
export const ksfxMajor02Select0 = 157; // soundeffects.h:163
export const ksfxMajor02Select1 = 158; // soundeffects.h:164
export const ksfxMajor02Select2 = 159; // soundeffects.h:165
export const ksfxMajor02Select3 = 160; // soundeffects.h:166
export const ksfxMajor02Move0 = 161; // soundeffects.h:167
export const ksfxMajor02Move1 = 162; // soundeffects.h:168
export const ksfxMajor02Move2 = 163; // soundeffects.h:169
export const ksfxMajor02Move3 = 164; // soundeffects.h:170
export const ksfxMajor02Attack0 = 165; // soundeffects.h:171
export const ksfxMajor02Attack1 = 166; // soundeffects.h:172
export const ksfxMajor02Attack2 = 167; // soundeffects.h:173
export const ksfxMajor02Attack3 = 168; // soundeffects.h:174
export const ksfxAndySelect = 169; // soundeffects.h:175
export const ksfxAndyMove = 170; // soundeffects.h:176
export const ksfxAndyAttack = 171; // soundeffects.h:177
export const ksfxAndyFire = 172; // soundeffects.h:178
export const ksfxAndyDestroyed = 173; // soundeffects.h:179
export const ksfxReplicatorOn = 174; // soundeffects.h:180
export const ksfxReplicatorOff = 175; // soundeffects.h:181
export const ksfxReplicatorBuild = 176; // soundeffects.h:182
export const ksfxActivatorOn = 177; // soundeffects.h:183
export const ksfxActivatorOff = 178; // soundeffects.h:184
export const ksfxArtilleryFire = 179; // soundeffects.h:185
export const ksfxArtilleryImpact = 180; // soundeffects.h:186
export const ksfxHappyEnding = 181; // soundeffects.h:187
export const ksfxReplicatorDestroyed = 182; // soundeffects.h:188
export const ksfxFoxDestroyed = 183; // soundeffects.h:189

/** One past the last Sfx id (184 ids, 0-183). */
export const ksfxMax = 184;

// game/soundeffects.h:197-221 enum SfxCategory (ksfxcNothing = -1)
export const ksfxcNothing = -1; // soundeffects.h:198
export const ksfxcInfantryDestroyed = 0; // soundeffects.h:199
export const ksfxcVehicleDestroyed = 1; // soundeffects.h:200
export const ksfxcMale01Select = 2; // soundeffects.h:201
export const ksfxcMale01Move = 3; // soundeffects.h:202
export const ksfxcMale01Attack = 4; // soundeffects.h:203
export const ksfxcMale03Select = 5; // soundeffects.h:204
export const ksfxcMale03Move = 6; // soundeffects.h:205
export const ksfxcMale03Attack = 7; // soundeffects.h:206
export const ksfxcMale06Select = 8; // soundeffects.h:207
export const ksfxcMale06Move = 9; // soundeffects.h:208
export const ksfxcMale06Attack = 10; // soundeffects.h:209
export const ksfxcMajor01Select = 11; // soundeffects.h:210
export const ksfxcMajor01Move = 12; // soundeffects.h:211
export const ksfxcMajor01Attack = 13; // soundeffects.h:212
export const ksfxcMajor02Select = 14; // soundeffects.h:213
export const ksfxcMajor02Move = 15; // soundeffects.h:214
export const ksfxcMajor02Attack = 16; // soundeffects.h:215
export const ksfxcAndySelect = 17; // soundeffects.h:216
export const ksfxcAndyMove = 18; // soundeffects.h:217
export const ksfxcAndyAttack = 19; // soundeffects.h:218
export const ksfxcAndyDestroyed = 20; // soundeffects.h:219
export const ksfxcFoxDestroyed = 21; // soundeffects.h:220

// game/misc.cpp:938-1125 — the hardwired sfx-category variant tables (gasfxce).
// Row index = SfxCategory; each row lists that category's interchangeable
// variant ids (struct SfxCategoryEntry { Sfx *asfx; int csfx; }).
export const gasfxce: readonly (readonly number[])[] = [
  // misc.cpp:943 gasfxInfantryDestroyed
  [
    ksfxInfantryDestroyed0,
    ksfxInfantryDestroyed1,
    ksfxInfantryDestroyed2,
    ksfxInfantryDestroyed3,
    ksfxInfantryDestroyed4,
  ],
  // misc.cpp:953 gasfxVehicleDestroyed
  [ksfxVehicleDestroyed],
  // misc.cpp:959-979 Male01
  [ksfxMale01Select0, ksfxMale01Select1, ksfxMale01Select2, ksfxMale01Select3],
  [ksfxMale01Move0, ksfxMale01Move1, ksfxMale01Move2, ksfxMale01Move3],
  [ksfxMale01Attack0, ksfxMale01Attack1, ksfxMale01Attack2, ksfxMale01Attack3],
  // misc.cpp:982-1002 Male03
  [ksfxMale03Select0, ksfxMale03Select1, ksfxMale03Select2, ksfxMale03Select3],
  [ksfxMale03Move0, ksfxMale03Move1, ksfxMale03Move2, ksfxMale03Move3],
  [ksfxMale03Attack0, ksfxMale03Attack1, ksfxMale03Attack2, ksfxMale03Attack3],
  // misc.cpp:1005-1025 Male06
  [ksfxMale06Select0, ksfxMale06Select1, ksfxMale06Select2, ksfxMale06Select3],
  [ksfxMale06Move0, ksfxMale06Move1, ksfxMale06Move2, ksfxMale06Move3],
  [ksfxMale06Attack0, ksfxMale06Attack1, ksfxMale06Attack2, ksfxMale06Attack3],
  // misc.cpp:1028-1048 Major01
  [ksfxMajor01Select0, ksfxMajor01Select1, ksfxMajor01Select2, ksfxMajor01Select3],
  [ksfxMajor01Move0, ksfxMajor01Move1, ksfxMajor01Move2, ksfxMajor01Move3],
  [ksfxMajor01Attack0, ksfxMajor01Attack1, ksfxMajor01Attack2, ksfxMajor01Attack3],
  // misc.cpp:1051-1071 Major02
  [ksfxMajor02Select0, ksfxMajor02Select1, ksfxMajor02Select2, ksfxMajor02Select3],
  [ksfxMajor02Move0, ksfxMajor02Move1, ksfxMajor02Move2, ksfxMajor02Move3],
  [ksfxMajor02Attack0, ksfxMajor02Attack1, ksfxMajor02Attack2, ksfxMajor02Attack3],
  // misc.cpp:1074-1089 Andy
  [ksfxAndySelect],
  [ksfxAndyMove],
  [ksfxAndyAttack],
  [ksfxAndyDestroyed],
  // misc.cpp:1092-1094 Fox
  [ksfxFoxDestroyed],
];

// game/misc.cpp:1127-1138 SfxFromCategory — pick a variant of the category with
// the ASYNC rng (misc.cpp:1133-1135: "We use GetAsyncRandom() to avoid
// disturbing the in-sync random number generator"; the pick is local-player
// cosmetic and differs per machine BY DESIGN — sim-quirks B19). rngAsync must
// be the glAsyncSeed instance (SimContext.rngAsync), NEVER the sync rng.
export function SfxFromCategory(sfxc: number, rngAsync: Rng): number {
  if (sfxc < 0 || sfxc >= gasfxce.length) return -1; // (Sfx)-1
  const psfxce = gasfxce[sfxc];
  return psfxce[rngAsync.GetRandom() % psfxce.length];
}
